import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  FiMenu,
  FiSearch,
  FiTruck,
  FiUsers,
  FiPackage,
  FiClipboard,
  FiUser,
  FiX,
} from "react-icons/fi";



import "./Header.css";

import {
  getCurrentUserId,
  fetchNotifications,
  subscribeToNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  fetchCurrentUser,
  globalSearch,
} from "../../services/header_service";

import { useSessionWatcher } from "../../services/useSessionWatcher";
import { useSidebar } from "../../contexts/SidebarContext";
import { playNotificationSound } from "../../pages/utils/notificationSound";

const typeIcon = {
  vehicle: "🚚",
  driver: "🪪",
  supplier: "📦",
  assignment: "📋",
  staff: "👤",
};

const typeLabel = {
  vehicle: "VEHICLE",
  driver: "DRIVER",
  supplier: "SUPPLIER",
  assignment: "ALLOTMENT",
  staff: "STAFF",
};

function BellIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
  );
}

function SearchResultIcon({ type }) {
  if (type === "vehicle") {
    return <FiTruck />;
  }

  if (type === "driver") {
    return <FiUsers />;
  }

  if (type === "supplier") {
    return <FiPackage />;
  }

  if (type === "assignment") {
    return <FiClipboard />;
  }

  return <FiUser />;
}

export default function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const { toggle } = useSidebar();

  useSessionWatcher();

  const [notifications, setNotifications] = useState([]);

  const getCachedCurrentUser = () => {
    const keys = [
      "currentUser",
      "current_user",
      "user",
      "loggedInUser",
      "logged_in_user",
      "auth_user",
    ];

    for (const key of keys) {
      try {
        const raw = localStorage.getItem(key);
        if (!raw) continue;

        const parsed = JSON.parse(raw);

        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          return parsed.user && typeof parsed.user === "object"
            ? { ...parsed, ...parsed.user }
            : parsed;
        }
      } catch {
        continue;
      }
    }

    const permissionsRaw = localStorage.getItem("permissions");
    let permissions = {};

    try {
      const parsed = JSON.parse(permissionsRaw || "{}");
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        permissions = parsed;
      }
    } catch {
      permissions = {};
    }

    const roleName =
      localStorage.getItem("role_name") ||
      localStorage.getItem("role") ||
      localStorage.getItem("user_role") ||
      permissions.role_name ||
      permissions.role ||
      "";

    const userId =
      localStorage.getItem("user_id") ||
      localStorage.getItem("userId") ||
      localStorage.getItem("current_user_id") ||
      localStorage.getItem("currentUserId") ||
      "";

    const fullName =
      localStorage.getItem("full_name") ||
      localStorage.getItem("name") ||
      localStorage.getItem("user_name") ||
      localStorage.getItem("username") ||
      "";

    if (userId || roleName || fullName) {
      return {
        id: userId ? Number(userId) || userId : undefined,
        full_name: fullName,
        name: fullName,
        role_name: roleName,
        role: roleName,
        user_role: roleName,
        permissions,
      };
    }

    return null;
  };

  const [notificationOpen, setNotificationOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(() => getCachedCurrentUser());

  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  const searchRef = useRef(null);
  const notificationRef = useRef(null);
  const hasLoadedOnce = useRef(false);
  const searchTimer = useRef(null);

  const pageTitles = [
    { path: "/dashboard", title: "Fleet Management Dashboard" },
    { path: "/drivers", title: "Driver Management" },
    { path: "/add-driver", title: "Add Driver" },
     { path: "/edit-driver", title: "Edit Driver" },
    { path: "/vehicles", title: "Vehicle Management" },
    { path: "/add-vehicle-documents", title: "Add Vehicle Documents" },
    { path: "/add-vehicle", title: "Add Vehicle" },
    // { path: "/add-vehicle", title: "Edit Vehicle" },
    { path: "/vehicle-documents", title: "Vehicle Documents" },
    { path: "/suppliers", title: "Suppliers" },
    { path: "/add-supplier", title: "Add Supplier" },
      { path: "/edit-supplier", title: "Edit Supplier" },
      { path: "/suppliers/outstanding", title: "Outstanding Update" },
    { path: "/assignments", title: "Trip Assignments" },
    {
      path: "/add-vehicle-assignment",
      title: "Add Vehicle Allocation",
    },
    {
      path: "/vehicle-assignment",
      title: "Vehicle Allocation",
    },
    { path: "/staff/add", title: "Add Staff" },
    { path: "/staff", title: "Staff Management" },
    { path: "/reports", title: "Reports" },
    { path: "/settings", title: "Settings" },
    { path: "/live-vehicles", title: "Live Tracking" },
    { path: "/audit-log", title: "Audit" },
    { path: "/master-data", title: "Master Data" },
    { path: "/messages", title: "Messages" },
    { path: "/recycle-bin", title: "Recycle Bin" },
    { path: "/fleet-by-branch", title: "Branch Overview" },
    { path: "/outstanding", title: "Outstanding Update" },
    { path: "/followups", title: "Follow-ups" },
      { path: "/global-search", title: "Global search" },
  ];

  const title =
    pageTitles
      .filter((item) =>
        location.pathname.startsWith(item.path)
      )
      .sort(
        (a, b) =>
          b.path.length - a.path.length
      )[0]?.title || "FleetManagementPro";

  useEffect(() => {
    const userId = getCurrentUserId();

    if (!userId) {
      return;
    }

    fetchNotifications(userId)
      .then((data) => {
        setNotifications(data || []);
        hasLoadedOnce.current = true;
      })
      .catch((error) => {
        console.error(
          "Failed to load notifications:",
          error
        );
      });
  }, []);

  useEffect(() => {
    let mounted = true;

    const loadCurrentUser = async () => {
      const cachedUser = getCachedCurrentUser();

      if (cachedUser && mounted) {
        setCurrentUser(cachedUser);
      }

      const userId = getCurrentUserId();

      if (!userId) {
        return;
      }

      try {
        const data = await fetchCurrentUser(userId);

        if (!mounted || !data) {
          return;
        }

        const apiUser =
          data?.user && typeof data.user === "object"
            ? { ...data, ...data.user }
            : data?.data && typeof data.data === "object"
              ? { ...data, ...data.data }
              : data;

        const normalizedUser = {
          ...(cachedUser || {}),
          ...apiUser,
          id:
            apiUser?.id ??
            apiUser?.user_id ??
            apiUser?.userId ??
            cachedUser?.id,
          full_name:
            apiUser?.full_name ??
            apiUser?.fullName ??
            apiUser?.name ??
            cachedUser?.full_name ??
            cachedUser?.name ??
            "",
          role_name:
            apiUser?.role_name ??
            apiUser?.roleName ??
            apiUser?.role?.role_name ??
            apiUser?.role?.name ??
            apiUser?.role ??
            apiUser?.user_role ??
            cachedUser?.role_name ??
            cachedUser?.role ??
            cachedUser?.user_role ??
            "",
          permissions:
            apiUser?.permissions ??
            cachedUser?.permissions ??
            {},
        };

        setCurrentUser(normalizedUser);
      } catch (error) {
        console.error("Failed to load current user:", error);

        if (mounted && cachedUser) {
          setCurrentUser(cachedUser);
        }
      }
    };

    loadCurrentUser();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const userId = getCurrentUserId();

    if (!userId) {
      return;
    }

    const unsubscribe =
      subscribeToNotifications(
        userId,
        (incoming) => {
          setNotifications((prev) => {
            if (
              prev.some(
                (item) =>
                  item.id === incoming.id
              )
            ) {
              return prev;
            }

            if (hasLoadedOnce.current) {
              playNotificationSound();
            }

            return [incoming, ...prev];
          });
        }
      );

    return unsubscribe;
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target)
      ) {
        setSearchOpen(false);
      }

      if (
        notificationRef.current &&
        !notificationRef.current.contains(
          event.target
        )
      ) {
        setNotificationOpen(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleClickOutside
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );
    };
  }, []);

  useEffect(() => {
    if (searchTimer.current) {
      clearTimeout(searchTimer.current);
    }

    const query = searchText.trim();

    if (query.length < 2) {
      setSearchResults([]);
      setSearchOpen(false);
      setSearchLoading(false);
      return;
    }

    setSearchOpen(true);
    setSearchLoading(true);

    searchTimer.current =
      setTimeout(async () => {
        try {
          const data =
            await globalSearch(query);

          setSearchResults(
            data?.results || []
          );
        } catch (error) {
          console.error(
            "Global search failed:",
            error
          );

          setSearchResults([]);
        } finally {
          setSearchLoading(false);
        }
      }, 350);

    return () => {
      if (searchTimer.current) {
        clearTimeout(searchTimer.current);
      }
    };
  }, [searchText]);

  const handleSearchResultClick =
    (result) => {
      setSearchText("");
      setSearchResults([]);
      setSearchOpen(false);

      if (result?.path) {
        navigate(result.path);
      }
    };

  const handleGlobalSearch = () => {
    const query = searchText.trim();

    if (query.length < 2) {
      return;
    }

    setSearchOpen(false);
    setSearchResults([]);
    navigate(`/global-search?q=${encodeURIComponent(query)}`);
  };

  const clearSearch = () => {
    setSearchText("");
    setSearchResults([]);
    setSearchOpen(false);
  };

  const unreadCount =
    notifications.filter(
      (item) => !item.read
    ).length;

  const handleNotificationClick =
    (note) => {
      setNotifications((prev) =>
        prev.filter(
          (item) =>
            item.id !== note.id
        )
      );

      setNotificationOpen(false);

      if (
        note.type ===
          "chat_message" &&
        note.link
      ) {
        const query =
          note.link.split("?")[1] ||
          "";

        const senderId =
          new URLSearchParams(
            query
          ).get("user");

        navigate("/messages", {
          state: {
            openUserId: senderId,
          },
        });
      } else if (note.link) {
        navigate(note.link);
      }

      markNotificationRead(
        note.id
      ).catch((error) =>
        console.error(
          "Failed to mark notification read:",
          error
        )
      );
    };

  const handleMarkAllRead = () => {
    const userId =
      getCurrentUserId();

    setNotifications([]);

    markAllNotificationsRead(
      userId
    ).catch((error) =>
      console.error(
        "Failed to mark all read:",
        error
      )
    );
  };

  const handleFleetXClick = () => {
    navigate("/live-vehicles");
  };

  return (
    <header className="header">

      <div className="header-left">

        <button
          type="button"
          className="header-menu-btn"
          onClick={toggle}
          aria-label="Toggle menu"
        >
          <FiMenu size={24} />
        </button>

        <div className="header-page-info">
          <h2>{title}</h2>
        </div>

      </div>

      <div className="header-center">

        <div
          className="global-search-wrapper"
          ref={searchRef}
        >

          <div
            className={`global-search ${
              searchOpen
                ? "search-open"
                : ""
            }`}
          >

            <div className="global-search-prefix">
              <img
                src="/assets/car-icon"
                alt="Vehicle"
                className="global-search-vehicle-icon"
              />
              <span className="global-search-label">
                Search Vehicle
              </span>
            </div>

            <input
              type="text"
              value={searchText}
              onChange={(event) =>
                setSearchText(
                  event.target.value
                )
              }
              onFocus={() => {
                if (
                  searchText.trim()
                    .length >= 2
                ) {
                  setSearchOpen(true);
                }
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleGlobalSearch();
                }

                if (event.key === "Escape") {
                  setSearchOpen(false);
                }
              }}
              placeholder="Registration number, vehicle code..."
              aria-label="Search vehicle"
            />

            {searchLoading && (
              <span className="global-search-loading">
                Searching…
              </span>
            )}

            {searchText &&
              !searchLoading && (
                <button
                  type="button"
                  className="global-search-clear"
                  onClick={clearSearch}
                  aria-label="Clear search"
                >
                  <FiX />
                </button>
              )}

          </div>

          {searchOpen && (
            <div className="global-search-dropdown">

              {searchLoading ? (

                <div className="global-search-state">
                  Searching...
                </div>

              ) : searchResults.length === 0 ? (

                <div className="global-search-state">

                  <FiSearch />

                  <span>
                    No matching records found
                  </span>

                </div>

              ) : (

                <>

                  <div className="global-search-heading">
                    Search Results
                  </div>

                  <div className="global-search-results">

                    {searchResults.map(
                      (result) => (

                        <button
                          type="button"
                          key={`${result.type}-${result.id}`}
                          className="global-search-result"
                          onClick={() =>
                            handleSearchResultClick(
                              result
                            )
                          }
                        >

                          <div className="global-search-result-icon">
                            <SearchResultIcon
                              type={
                                result.type
                              }
                            />
                          </div>

                          <div className="global-search-result-content">

                            <div className="global-search-result-top">

                              <strong>
                                {result.title}
                              </strong>

                              <span className="global-search-result-type">
                                {typeLabel[
                                  result.type
                                ] ||
                                  result.type}
                              </span>

                            </div>

                            {result.subtitle && (
                              <div className="global-search-result-subtitle">
                                {
                                  result.subtitle
                                }
                              </div>
                            )}

                            {result.meta && (
                              <div className="global-search-result-meta">
                                {result.meta}
                              </div>
                            )}

                          </div>

                        </button>

                      )
                    )}

                  </div>

                  {searchResults.length > 0 && (
                    <button
                      type="button"
                      className="global-search-view-all"
                      onClick={handleGlobalSearch}
                    >
                      <span>View all search results</span>
                      <span className="global-search-view-all-arrow">→</span>
                    </button>
                  )}

                </>

              )}

            </div>
          )}

        </div>

        <button
          type="button"
          className="fleetx-header-btn"
          onClick={handleFleetXClick}
          title="Open FleetX Live Tracking"
        >

          <span className="fleetx-header-icon">
            <img
              src="https://blog.fleetx.ai/content/images/2019/05/fleetx-logo-blog.png"
              alt="Fleetx"
            />
          </span>

          <span className="fleetx-header-text">
             Live Tracking
          </span>

        </button>

      </div>

      <div className="header-right">

        <div
          className="notify-wrapper"
          ref={notificationRef}
        >

          <button
            type="button"
            className={`notify-btn ${
              notificationOpen
                ? "active"
                : ""
            }`}
            onClick={() =>
              setNotificationOpen(
                (prev) => !prev
              )
            }
            aria-label="Notifications"
          >

            <BellIcon />

            {unreadCount > 0 && (
              <span className="notify-badge">
                {unreadCount > 9
                  ? "9+"
                  : unreadCount}
              </span>
            )}

          </button>

          {notificationOpen && (
            <div className="notify-dropdown">

              <div className="notify-dropdown-header">

                <span>
                  Notifications
                </span>

                {unreadCount > 0 && (
                  <button
                    type="button"
                    className="notify-mark-read"
                    onClick={
                      handleMarkAllRead
                    }
                  >
                    Mark all read
                  </button>
                )}

              </div>

              <div className="notify-list">

                {notifications.length ===
                0 ? (

                  <div className="notify-empty">
                    No notifications
                  </div>

                ) : (

                  notifications.map(
                    (note) => (

                      <div
                        key={note.id}
                        className={`notify-item ${
                          note.read
                            ? ""
                            : "unread"
                        }`}
                        onClick={() =>
                          handleNotificationClick(
                            note
                          )
                        }
                      >

                        <span className="notify-icon">
                          {typeIcon[
                            note.type
                          ] || "🔔"}
                        </span>

                        <div className="notify-content">

                          {note.title && (
                            <p className="notify-title">
                              {note.title}
                            </p>
                          )}

                          <p className="notify-message">
                            {note.message}
                          </p>

                          <span className="notify-time">
                            {new Date(
                              note.createdAt
                            ).toLocaleDateString(
                              "en-IN",
                              {
                                day: "numeric",
                                month: "short",
                              }
                            )}
                          </span>

                        </div>

                        {!note.read && (
                          <span className="notify-dot" />
                        )}

                      </div>

                    )
                  )

                )}

              </div>

            </div>
          )}

        </div>

        <div className="profile">

          <img
            src="/assets/logo.jpg"
            alt="Logo"
            className="profile-logo"
          />

          <div className="profile-info">

            <span className="profile-name">
              {currentUser?.full_name ||
                currentUser?.name ||
                "User"}
            </span>

            <span className="profile-role">
              {(() => {
                const rawRole =
                  currentUser?.role_name ??
                  currentUser?.roleName ??
                  currentUser?.role?.role_name ??
                  currentUser?.role?.name ??
                  (typeof currentUser?.role === "string"
                    ? currentUser.role
                    : "") ??
                  currentUser?.user_role ??
                  "";

                const normalizedRole = String(rawRole)
                  .trim()
                  .toLowerCase()
                  .replace(/[_-]+/g, " ")
                  .replace(/\s+/g, " ");

                if (normalizedRole === "superadmin" || normalizedRole === "super admin") {
                  return "Superadmin";
                }

                if (normalizedRole === "admin") {
                  return "Admin";
                }

                if (normalizedRole === "staff") {
                  return "Staff";
                }

                return rawRole
                  ? String(rawRole)
                      .trim()
                      .replace(/[_-]+/g, " ")
                      .replace(/\s+/g, " ")
                  : "";
              })()}
            </span>

          </div>

        </div>

      </div>

    </header>
  );
}